import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import GoogleMapsWrapper from './GoogleMapsWrapper';

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  onLocationChange: (location: {
    latitude: number;
    longitude: number;
    address: string;
    country?: string;
    region?: string;
    city?: string;
  }) => void;
  className?: string;
}

interface GoogleMapProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  onLocationChange: (location: any) => void;
}

const GoogleMapComponent: React.FC<GoogleMapProps> = ({
  center,
  zoom,
  onLocationChange
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current && !map) {
      const mapInstance = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      setMap(mapInstance);

      // Create marker
      const markerInstance = new window.google.maps.Marker({
        position: center,
        map: mapInstance,
        draggable: true,
        title: 'Incident Location'
      });

      setMarker(markerInstance);

      // Handle marker drag
      markerInstance.addListener('dragend', () => {
        const position = markerInstance.getPosition();
        if (position) {
          reverseGeocode(position.lat(), position.lng(), mapInstance);
        }
      });

      // Handle map click
      mapInstance.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          markerInstance.setPosition({ lat, lng });
          reverseGeocode(lat, lng, mapInstance);
        }
      });

      // Initialize Places Search Box
      if (searchInputRef.current) {
        const searchBoxInstance = new window.google.maps.places.SearchBox(searchInputRef.current);

        searchBoxInstance.addListener('places_changed', () => {
          const places = searchBoxInstance.getPlaces();
          if (!places || places.length === 0) return;

          const place = places[0];
          if (!place.geometry || !place.geometry.location) return;

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          // Update map and marker
          mapInstance.setCenter({ lat, lng });
          mapInstance.setZoom(15);
          markerInstance.setPosition({ lat, lng });

          // Extract address components
          extractAddressInfo(place, lat, lng);
        });

        // Bias the SearchBox results towards current map's viewport
        mapInstance.addListener('bounds_changed', () => {
          searchBoxInstance.setBounds(mapInstance.getBounds()!);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (map && marker) {
      map.setCenter(center);
      marker.setPosition(center);
    }
  }, [center, map, marker]);

  const reverseGeocode = async (lat: number, lng: number, mapInstance: google.maps.Map) => {
    const geocoder = new window.google.maps.Geocoder();

    try {
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results && response.results.length > 0) {
        const result = response.results[0];
        extractAddressInfo(result, lat, lng);
      } else {
        onLocationChange({
          latitude: lat,
          longitude: lng,
          address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        });
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      onLocationChange({
        latitude: lat,
        longitude: lng,
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      });
    }
  };

  // Countries where Google's administrative_area_level_1 is supra-provincial
  // (e.g. PH "Central Luzon", ES "Cataluña", FR "Occitanie", BD "Dhaka Division"),
  // so the user-meaningful province/département/district lives in level_2.
  const PROVINCE_IS_LEVEL_2 = new Set(['PH', 'ES', 'FR', 'BD']);

  const extractAddressInfo = (place: google.maps.places.PlaceResult | google.maps.GeocoderResult, lat: number, lng: number) => {
    const addressComponents = place.address_components || [];

    let country = '';
    let countryCode = '';
    let adminLevel1 = '';
    let adminLevel2 = '';
    let locality = '';
    let postalTown = '';
    let sublocality = '';

    addressComponents.forEach(component => {
      const types = component.types;

      if (types.includes('country')) {
        country = component.long_name;
        countryCode = component.short_name;
      } else if (types.includes('administrative_area_level_1')) {
        adminLevel1 = component.long_name;
      } else if (types.includes('administrative_area_level_2')) {
        adminLevel2 = component.long_name;
      } else if (types.includes('locality')) {
        locality = component.long_name;
      } else if (types.includes('postal_town')) {
        postalTown = component.long_name;
      } else if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
        sublocality = component.long_name;
      }
    });

    const region = PROVINCE_IS_LEVEL_2.has(countryCode)
      ? (adminLevel2 || adminLevel1)
      : (adminLevel1 || adminLevel2);

    const city = locality || postalTown || sublocality || '';

    onLocationChange({
      latitude: lat,
      longitude: lng,
      address: place.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      country,
      region,
      city
    });
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-start space-x-2">
          <MapPinIcon className="h-4 w-4 mt-0.5 text-gray-400" />
          <div>
            <p className="font-medium">How to set location:</p>
            <ul className="mt-1 space-y-1 text-xs">
              <li>• Search for an address in the search box below</li>
              <li>• Click anywhere on the map to place a pin</li>
              <li>• Drag the pin to adjust the exact location</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div ref={ref} className="w-full h-96 rounded-lg border border-gray-300" />

      {/* Search Box */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for an address or place..."
          className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>
    </div>
  );
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude = 0,
  longitude = 0,
  address = '',
  onLocationChange,
  className = ''
}) => {
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: latitude || 0,
    lng: longitude || 0
  });

  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter({ lat: latitude, lng: longitude });
    } else {
      // Default to a reasonable center (somewhere in South America for IPRI context)
      setMapCenter({ lat: -14.2350, lng: -51.9253 });
    }
  }, [latitude, longitude]);

  const handleLocationChange = (location: any) => {
    setMapCenter({ lat: location.latitude, lng: location.longitude });
    onLocationChange(location);
  };

  return (
    <div className={className}>
      <GoogleMapsWrapper>
        <GoogleMapComponent
          center={mapCenter}
          zoom={latitude && longitude ? 15 : 2}
          onLocationChange={handleLocationChange}
        />
      </GoogleMapsWrapper>
    </div>
  );
};

export default LocationPicker;